import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { STATUS_META } from "@/components/StatusBadge";
import { Search, Package, Truck, CheckCircle2, Clock } from "lucide-react";
import Logo from "@/components/Logo";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate, fmtDateTime } from "@/lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKFLOW = [
  "confirmed",
  "design",
  "customer_approval",
  "printing",
  "finishing",
  "quality_check",
  "packing",
  "ready",
  "out_for_delivery",
  "delivered",
];

export default function PublicTracking() {
  const { id } = useParams();
  usePageTitle(id ? `Track ${id}` : "Track Order");
  const nav = useNavigate();
  const [orderNo, setOrderNo] = useState(id || "");
  const [identifier, setIdentifier] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // If someone hits /track/:id directly, try legacy public lookup for backward compatibility.
  useEffect(() => {
    if (!id) return;
    setErr("");
    axios.get(`${API}/track/${id}`).then((r) => setData(r.data)).catch(() => {
      setData(null);
      setErr("Please enter your registered mobile or email above and the order number to view details.");
    });
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setData(null);
    if (!identifier.trim() || !orderNo.trim()) {
      toast.error("Both mobile/email and order number are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/track/lookup`, {
        identifier: identifier.trim(),
        order_no: orderNo.trim(),
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No order found matching those details.";
      setErr(msg);
    } finally { setLoading(false); }
  };

  const currentIdx = data ? WORKFLOW.indexOf(data.status) : -1;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <header className="border-b border-[hsl(var(--border))] px-6 lg:px-14 py-5 flex items-center justify-between">
        <div className="bg-black px-4 py-3 inline-flex">
          <Logo height={40} variant="dark" />
        </div>
        <a href="/login" className="btn-swiss btn-outline" data-testid="staff-login-link">
          Login
        </a>
      </header>

      <section className="px-6 lg:px-14 py-16 lg:py-24 border-b border-[hsl(var(--border))]">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Public Order Tracking</div>
            <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95]">
              Track your
              <br />
              <span className="text-[#FF6B00]">print job</span> live.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-lg">
              Enter your order ID (e.g. <span className="font-mono">BA-2026-000001</span>) and see
              every stage from design to delivery.
            </p>
          </div>
          <form onSubmit={submit} className="lg:col-span-5" data-testid="track-form">
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">Registered Mobile / Email</label>
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="+91 8070 970 980 or you@brand.com"
                  className="w-full px-4 py-3 border-2 border-zinc-300 focus:border-[#FF6B00] rounded-lg outline-none bg-white"
                  data-testid="track-identifier"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">Order Number</label>
                <input
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value.toUpperCase())}
                  placeholder="SO-26-00001 or BA-2026-000001"
                  className="w-full px-4 py-3 border-2 border-zinc-300 focus:border-[#FF6B00] rounded-lg outline-none bg-white font-mono text-sm"
                  data-testid="track-order-no"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6B00] hover:bg-[#e65a00] text-white px-6 py-3.5 font-bold rounded-lg text-sm uppercase tracking-widest disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="track-submit"
              >
                <Search size={14} /> {loading ? "Looking up…" : "Track My Order"}
              </button>
              <div className="text-[10px] text-zinc-500 text-center">
                Don't have the order number? Call our Customer Care <a href="tel:02247427059" className="text-[#FF6B00] font-bold hover:underline">022 4742 7059</a> or <a href="https://wa.me/918070970980" target="_blank" rel="noreferrer" className="text-[#25D366] font-bold hover:underline">WhatsApp us</a>
              </div>
            </div>
          </form>
        </div>
      </section>

      <main className="px-6 lg:px-14 py-14">
        {err && (
          <div className="text-center py-16 border border-dashed border-[hsl(var(--border))]" data-testid="track-error">
            <div className="font-display text-2xl font-bold mb-2">Not found</div>
            <div className="text-sm text-muted-foreground">{err}</div>
          </div>
        )}

        {data && (
          <div className="space-y-10" data-testid="track-result">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="card-swiss p-6 md:col-span-2">
                <div className="overline mb-2 font-mono">{data.order_no || data.id}</div>
                <div className="font-display text-3xl font-black tracking-tighter">
                  {data.type === "shop_order"
                    ? `${data.items?.length || 0} item${(data.items?.length || 0) === 1 ? "" : "s"}`
                    : (data.product_name || "Print Order")}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {data.type === "shop_order"
                    ? `Shipping to ${data.address?.city}, ${data.address?.state}`
                    : (data.customer_name ? `for ${data.customer_name}` : "")}
                </div>
                {data.type === "shop_order" && (
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    {data.items?.slice(0, 4).map((it, i) => (
                      <div key={i} className="flex items-center gap-2 bg-zinc-50 rounded-lg p-2">
                        <Package size={14} className="text-[#FF6B00]" />
                        <div className="flex-1 truncate">
                          <div className="font-semibold text-xs truncate">{it.name}</div>
                          <div className="text-[10px] text-zinc-500">Qty {it.quantity}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 font-mono text-xs text-muted-foreground">Placed: {data.created_at ? fmtDateTime(data.created_at) : "—"}</div>
              </div>
              <div className="card-swiss p-6">
                <div className="overline mb-2">Current Status</div>
                <div className="font-display font-black text-2xl text-[#FF6B00]">
                  {STATUS_META[data.status]?.label || (data.status || "").replace(/_/g, " ")}
                </div>
                {data.payment_status && (
                  <div className={`text-xs font-bold mt-2 ${data.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>Payment: {data.payment_status.toUpperCase()}</div>
                )}
                {data.total > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase text-zinc-500">Total</div>
                    <div className="font-mono font-black text-xl">₹{Math.round(data.total).toLocaleString("en-IN")}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline steps — only for ERP orders */}
            {data.type !== "shop_order" && (
            <div className="card-swiss p-6">
              <div className="overline mb-6">Progress</div>
              <div className="space-y-4">
                {WORKFLOW.map((s, i) => {
                  const done = i <= currentIdx;
                  const active = i === currentIdx;
                  return (
                    <div key={s} className="flex items-center gap-4">
                      <div
                        className="w-8 h-8 flex items-center justify-center font-mono text-xs font-bold"
                        style={{
                          background: done ? "#FF6B00" : "hsl(var(--muted))",
                          color: done ? "white" : "hsl(var(--muted-foreground))",
                          border: active ? "2px solid #FF6B00" : "1px solid hsl(var(--border))",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1">
                        <div className={`font-semibold ${done ? "" : "text-muted-foreground"}`}>
                          {STATUS_META[s]?.label}
                        </div>
                      </div>
                      {done && <div className="text-xs text-muted-foreground">✓ Complete</div>}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            <div className="card-swiss p-6">
              <div className="overline mb-4">History</div>
              <div className="space-y-3">
                {data.timeline?.slice().reverse().map((t, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm border-l-2 pl-3" style={{ borderColor: STATUS_META[t.status]?.color || "#71717A" }}>
                    <div className="flex-1">
                      <div className="font-semibold">{STATUS_META[t.status]?.label || t.status}</div>
                      <div className="text-xs text-muted-foreground">{t.note}</div>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {fmtDateTime(t.at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-[hsl(var(--border))] px-6 lg:px-14 py-8 text-xs text-muted-foreground flex flex-wrap justify-between gap-4">
        <div>© 2026 Brothers Advertising · Printing · Branding · Signage</div>
        <div>Support: print@brothersdigital.in</div>
      </footer>
    </div>
  );
}
